import React from 'react';
import BaseTable, {
  AutoResizer,
  type BaseTableProps,
  type AutoResizerProps,
} from 'react-base-table';
import { TableWrapper } from './styled';

// Typescript Omit doesn't work well with BaseTableProps since there are index signatures in BaseTableProps,
// it will cause the index properties to override all the properties in BaseTableProps.
type OmitWithFixing<T, K extends keyof T> = {
  [P in keyof T as P extends K ? never : P]: T[P];
};

export interface RefForwardedTableProps<T = unknown>
  extends OmitWithFixing<BaseTableProps<T>, 'width' | 'height'> {
  onResize?: AutoResizerProps['onResize'];
}

export interface RefForwardedTableRef<T = unknown> extends BaseTable<T> {}

const RefForwardedBaseTable = React.forwardRef<RefForwardedTableRef, RefForwardedTableProps>(
  (props, ref) => {
    const { className, style, onResize, ...tableProps } = props;

    return (
      <TableWrapper className={className} style={style}>
        <AutoResizer onResize={onResize}>
          {({ width, height }) => (
            <BaseTable {...tableProps} ref={ref} width={width} height={height} />
          )}
        </AutoResizer>
      </TableWrapper>
    );
  },
);

RefForwardedBaseTable.displayName = 'RefForwardedBaseTable';

// React hoc memo and forwardRef will cause that the generics type to be lost, so force to assign the type here.
export default React.memo(RefForwardedBaseTable) as <T = unknown>(
  props: RefForwardedTableProps<T> & { ref?: React.ForwardedRef<RefForwardedTableRef<T>> },
) => JSX.Element;
